import React from "react";
import { Box, Tooltip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import useTranslation from "../../../shared/hooks/useTranslation";
import { dt } from "../utils/dt";

// =====================================================================
//  COLOR MAP — maps backend statusColor string to hex values
// =====================================================================

const STATUS_COLORS: Record<string, string> = {
  primary: dt.blue,  // InProgress — blue
  info: "#00C1F6",     // OpenToOffers — light blue
  warning: "#F59E0B",  // Negotiating, AwaitingConfirmation — amber
  default: "#747881",  // Draft — gray
};

// =====================================================================
//  COMPONENT
// =====================================================================

interface SrStatusBadgeProps {
  machineId: string;
  /** e.g., "In Progress", "Open for Offers", "Negotiating" */
  serviceRequestStatus?: string;
  /** e.g., "primary", "info", "warning", "default" */
  serviceRequestStatusColor?: string;
  /** Number of active service requests for this machine */
  activeServiceRequestCount?: number;
}

const SrStatusBadge = ({
  machineId,
  serviceRequestStatus,
  serviceRequestStatusColor,
  activeServiceRequestCount,
}: SrStatusBadgeProps) => {
  const navigate = useNavigate();
  const { translate } = useTranslation("myMachinery");

  if (!serviceRequestStatus || !activeServiceRequestCount) return null;

  const color = STATUS_COLORS[serviceRequestStatusColor || "default"] || STATUS_COLORS.default;

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    navigate(`/machinery/${machineId}?tab=service-reports`);
  };

  // Build the label text
  const label =
    activeServiceRequestCount === 1
      ? serviceRequestStatus
      : translate("activeServiceRequests", { count: activeServiceRequestCount });

  return (
    <Tooltip title={translate("viewServiceRequests")} arrow placement="top">
      <Box
        onClick={handleClick}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          cursor: "pointer",
          padding: "3px 10px",
          borderRadius: "12px",
          backgroundColor: `${color}14`,
          border: `1px solid ${color}30`,
          transition: "all 0.2s ease",
          "&:hover": {
            backgroundColor: `${color}22`,
            transform: "translateY(-1px)",
          },
        }}
      >
        {/* Colored dot */}
        <Box
          sx={{
            width: "7px",
            height: "7px",
            borderRadius: "50%",
            backgroundColor: color,
            boxShadow: `0 0 4px ${color}60`,
            flexShrink: 0,
          }}
        />
        {/* Label text */}
        <Box
          sx={{
            fontSize: "11px",
            fontFamily: dt.font,
            fontWeight: 500,
            color: color,
            whiteSpace: "nowrap",
            lineHeight: "16px",
            letterSpacing: "0.1px",
          }}
        >
          {label}
        </Box>
      </Box>
    </Tooltip>
  );
};

export default SrStatusBadge;
